/**
 * Always-on side rail: Excel-fallback PPW / EPC / Y1 monthly for Enfin + LightReach.
 * Default: rate dropdown + escalator chips + partner cards for the selected rate.
 * Grid tab: full rate × escalator matrix for one partner.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CurrencyDollar, WarningCircle, X } from '@phosphor-icons/react';
import type { PreContractIntakeForm } from '../lib/preContractTypes';
import {
    buildPartnerPpwMatrix,
    buildPreContractLivePpwQuotes,
    formatEscalatorPercent,
    formatPpaRate,
    sortPartnerQuotesForDisplay,
    type PreContractPpwMetricsOverride,
    type PpwMatrixCell,
    type PpwPartnerId,
    type PpwPartnerQuote,
} from '../lib/preContractPpwPricing';

export type PreContractLivePricingRailProps = {
    form: PreContractIntakeForm;
    /** When set, renders as side-panel content with a close control. */
    onClose?: () => void;
    metricsOverride?: PreContractPpwMetricsOverride | null;
    /** Energy community sites should recommend Enfin without hiding other partners. */
    preferEnfinPartner?: boolean;
};

/** `all-in` uses form.utilityRate; otherwise a numeric table rate. */
type RateSelection = 'all-in' | number;
type PricingView = 'partners' | 'grid';

function formatMoney(value: number | null | undefined, digits = 0): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return value.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: digits,
        minimumFractionDigits: digits,
    });
}

function formatPpw(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return '—';
    return `$${value.toFixed(2)}/W`;
}

function nearlyEqual(a: number, b: number, eps = 1e-6): boolean {
    return Math.abs(a - b) <= eps;
}

function PartnerCard({ quote, recommended }: { quote: PpwPartnerQuote; recommended?: boolean }) {
    return (
        <article
            className={`pci-live-pricing__card${
                quote.available ? '' : ' pci-live-pricing__card--muted'
            }${recommended ? ' pci-live-pricing__card--recommended' : ''}`}
        >
            <header className="pci-live-pricing__card-head">
                <h3>{quote.partner}</h3>
                {recommended ? (
                    <span className="pci-live-pricing__chip pci-live-pricing__chip--recommended">
                        Recommended
                    </span>
                ) : null}
                {quote.interpolated ? (
                    <span className="pci-live-pricing__chip">Rate interpolated</span>
                ) : null}
            </header>
            {quote.available ? (
                <dl className="pci-live-pricing__metrics">
                    <div>
                        <dt>PPW</dt>
                        <dd>{formatPpw(quote.ppw)}</dd>
                    </div>
                    <div>
                        <dt>EPC</dt>
                        <dd>{formatMoney(quote.epc, 0)}</dd>
                    </div>
                    <div>
                        <dt>Y1 monthly</dt>
                        <dd>{formatMoney(quote.estimatedMonthly, 0)}</dd>
                    </div>
                </dl>
            ) : (
                <p className="pci-live-pricing__reason" role="status">
                    <WarningCircle size={14} weight="fill" aria-hidden />
                    {quote.reason || 'Unavailable'}
                </p>
            )}
        </article>
    );
}

export const PreContractLivePricingRail: React.FC<PreContractLivePricingRailProps> = ({
    form,
    onClose,
    metricsOverride = null,
    preferEnfinPartner = false,
}) => {
    const [view, setView] = useState<PricingView>('partners');
    const [gridPartner, setGridPartner] = useState<PpwPartnerId>('LightReach');
    const gridPartnerDefaultedRef = useRef(false);
    const [escalator, setEscalator] = useState(0.0299);
    const [rateSelection, setRateSelection] = useState<RateSelection>('all-in');

    const rateOverride = rateSelection === 'all-in' ? null : rateSelection;
    const result = useMemo(
        () => buildPreContractLivePpwQuotes(form, escalator, rateOverride, metricsOverride),
        [form, escalator, rateOverride, metricsOverride],
    );

    const matrix = useMemo(() => {
        if (view !== 'grid' || !result.state) return null;
        return buildPartnerPpwMatrix({
            partner: gridPartner,
            state: result.state,
            multiplier: result.multiplier,
            systemWatts: result.systemWatts,
            annualProductionKwh: result.annualProductionKwh,
            extraRate: result.allInRate > 0 ? result.allInRate : null,
        });
    }, [view, gridPartner, result]);

    const displayQuotes = useMemo(
        () => sortPartnerQuotesForDisplay(result.quotes, { preferEnfin: preferEnfinPartner }),
        [result.quotes, preferEnfinPartner],
    );

    useEffect(() => {
        if (preferEnfinPartner && !gridPartnerDefaultedRef.current) {
            setGridPartner('Enfin');
            gridPartnerDefaultedRef.current = true;
        }
    }, [preferEnfinPartner]);

    useEffect(() => {
        setRateSelection('all-in');
    }, [form.utilityRate, form.installState]);

    useEffect(() => {
        if (!result.availableEscalators.length) return;
        if (!result.availableEscalators.some((e) => nearlyEqual(e, escalator, 1e-4))) {
            const preferred = result.availableEscalators.find((e) => nearlyEqual(e, 0.0299, 1e-4));
            setEscalator(preferred ?? result.availableEscalators[result.availableEscalators.length - 1]);
        }
    }, [result.availableEscalators, escalator]);

    const rateSelectValue = rateSelection === 'all-in' ? 'all-in' : String(rateSelection);

    const selectCell = (cell: PpwMatrixCell) => {
        if (!cell.available) return;
        setEscalator(cell.escalator);
        if (result.allInRate > 0 && nearlyEqual(cell.rate, result.allInRate)) {
            setRateSelection('all-in');
        } else {
            setRateSelection(cell.rate);
        }
    };

    return (
        <section
            className={`pci-live-pricing-rail${
                onClose ? ' pci-live-pricing-rail--panel' : ''
            }${view === 'grid' ? ' pci-live-pricing-rail--grid' : ''}`}
            aria-label="Live finance partner pricing"
        >
            <header className="pci-live-pricing__head">
                <div className="pci-live-pricing__head-top">
                    <div className="pci-live-pricing__title-row">
                        <CurrencyDollar size={16} weight="bold" aria-hidden />
                        <div>
                            {onClose ? (
                                <p className="pci-board-v2-label">Finance partner pricing</p>
                            ) : null}
                            <h2 className="pci-live-pricing__title">Live pricing</h2>
                        </div>
                    </div>
                    {onClose ? (
                        <button
                            type="button"
                            className="pci-live-pricing__close"
                            onClick={onClose}
                            aria-label="Close pricing"
                        >
                            <X size={18} weight="bold" />
                        </button>
                    ) : null}
                </div>
                <div className="pci-live-pricing__view-tabs" role="tablist" aria-label="Pricing view">
                    <button
                        type="button"
                        role="tab"
                        className={`pci-live-pricing__view-tab${
                            view === 'partners' ? ' pci-live-pricing__view-tab--on' : ''
                        }`}
                        aria-selected={view === 'partners'}
                        onClick={() => setView('partners')}
                    >
                        Partners
                    </button>
                    <button
                        type="button"
                        role="tab"
                        className={`pci-live-pricing__view-tab${
                            view === 'grid' ? ' pci-live-pricing__view-tab--on' : ''
                        }`}
                        aria-selected={view === 'grid'}
                        onClick={() => setView('grid')}
                    >
                        Grid
                    </button>
                </div>
            </header>

            {view === 'partners' ? (
                <div className="pci-live-pricing__inputs" aria-label="Pricing inputs">
                    <div>
                        <span>State</span>
                        <strong>{result.state || '—'}</strong>
                    </div>
                    <div className="pci-live-pricing__input--rate">
                        <span>Rate</span>
                        <label className="pci-live-pricing__rate-label">
                            <span className="pci-sr-only">PPA rate</span>
                            <select
                                className="pci-live-pricing__rate-select"
                                value={rateSelectValue}
                                aria-label="PPA rate"
                                onChange={(event) => {
                                    const value = event.target.value;
                                    if (value === 'all-in') {
                                        setRateSelection('all-in');
                                        return;
                                    }
                                    const next = Number(value);
                                    if (Number.isFinite(next) && next > 0) {
                                        setRateSelection(next);
                                    }
                                }}
                            >
                                <option value="all-in">
                                    {result.allInRate > 0
                                        ? `All-in ${formatPpaRate(result.allInRate)}`
                                        : 'All-in rate'}
                                </option>
                                {result.availableRates.map((rate) => (
                                    <option key={rate} value={String(rate)}>
                                        {formatPpaRate(rate)}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    <div>
                        <span>Size</span>
                        <strong>
                            {result.systemKw > 0 ? `${result.systemKw.toFixed(2)} kW` : '—'}
                        </strong>
                    </div>
                    <div>
                        <span>Production</span>
                        <strong>
                            {result.annualProductionKwh > 0
                                ? `${Math.round(result.annualProductionKwh).toLocaleString()} kWh`
                                : '—'}
                        </strong>
                    </div>
                    <div>
                        <span>Multiplier</span>
                        <strong>
                            {result.multiplier > 0 ? result.multiplier.toFixed(3) : '—'}
                        </strong>
                    </div>
                </div>
            ) : (
                <dl className="pci-live-pricing__meta" aria-label="Design pricing inputs">
                    <div>
                        <dt>State</dt>
                        <dd>{result.state || '—'}</dd>
                    </div>
                    <div>
                        <dt>All-in</dt>
                        <dd>
                            {result.allInRate > 0 ? formatPpaRate(result.allInRate) : '—'}
                        </dd>
                    </div>
                    <div>
                        <dt>Size</dt>
                        <dd>
                            {result.systemKw > 0 ? `${result.systemKw.toFixed(2)} kW` : '—'}
                        </dd>
                    </div>
                    <div>
                        <dt>Prod</dt>
                        <dd>
                            {result.annualProductionKwh > 0
                                ? `${Math.round(result.annualProductionKwh).toLocaleString()} kWh`
                                : '—'}
                        </dd>
                    </div>
                    <div>
                        <dt>Mult</dt>
                        <dd>
                            {result.multiplier > 0 ? result.multiplier.toFixed(3) : '—'}
                        </dd>
                    </div>
                </dl>
            )}

            {view === 'partners' && result.availableEscalators.length > 0 ? (
                <div className="pci-live-pricing__escalators" role="group" aria-label="Escalator">
                    {result.availableEscalators.map((value) => (
                        <button
                            key={value}
                            type="button"
                            className={`pci-live-pricing__esc-btn${
                                nearlyEqual(value, result.escalator, 1e-4)
                                    ? ' pci-live-pricing__esc-btn--on'
                                    : ''
                            }`}
                            aria-pressed={nearlyEqual(value, result.escalator, 1e-4)}
                            onClick={() => setEscalator(value)}
                        >
                            {formatEscalatorPercent(value)}
                        </button>
                    ))}
                </div>
            ) : null}

            {view === 'partners' && result.availableEscalators.length === 0 ? (
                <p className="pci-live-pricing__reason" role="status">
                    <WarningCircle size={14} weight="fill" aria-hidden />
                    No Excel tables for this state yet.
                </p>
            ) : null}

            {view === 'partners' ? (
                <div className="pci-live-pricing__cards">
                    {preferEnfinPartner ? (
                        <p className="pci-live-pricing__ec-note" role="status">
                            Enfin is recommended for energy community properties. Other partners remain
                            available.
                        </p>
                    ) : null}
                    {displayQuotes.map((quote) => (
                        <PartnerCard
                            key={quote.partner}
                            quote={quote}
                            recommended={
                                preferEnfinPartner &&
                                quote.partner === 'Enfin' &&
                                quote.available
                            }
                        />
                    ))}
                </div>
            ) : (
                <>
                    <div
                        className="pci-live-pricing__partner-tabs"
                        role="tablist"
                        aria-label="Finance partner"
                    >
                        {(['LightReach', 'Enfin'] as PpwPartnerId[]).map((id) => (
                            <button
                                key={id}
                                type="button"
                                role="tab"
                                className={`pci-live-pricing__partner-tab${
                                    gridPartner === id ? ' pci-live-pricing__partner-tab--on' : ''
                                }`}
                                aria-selected={gridPartner === id}
                                onClick={() => setGridPartner(id)}
                            >
                                {id}
                            </button>
                        ))}
                    </div>

                    {!matrix || matrix.rates.length === 0 || matrix.escalators.length === 0 ? (
                        <p className="pci-live-pricing__reason" role="status">
                            <WarningCircle size={14} weight="fill" aria-hidden />
                            No Excel tables for this state yet.
                        </p>
                    ) : (
                        <div className="pci-live-pricing__matrix-wrap">
                            <table className="pci-live-pricing__matrix">
                                <caption className="pci-sr-only">
                                    {gridPartner} pricing by rate and escalator
                                </caption>
                                <thead>
                                    <tr>
                                        <th scope="col">Rate</th>
                                        {matrix.escalators.map((esc) => (
                                            <th key={esc} scope="col">
                                                {formatEscalatorPercent(esc)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {matrix.rates.map((rate, rateIdx) => {
                                        const isAllIn =
                                            result.allInRate > 0 &&
                                            nearlyEqual(rate, result.allInRate);
                                        return (
                                            <tr
                                                key={rate}
                                                className={
                                                    isAllIn
                                                        ? 'pci-live-pricing__matrix-row--allin'
                                                        : undefined
                                                }
                                            >
                                                <th scope="row">
                                                    {formatPpaRate(rate)}
                                                    {isAllIn ? <em>all-in</em> : null}
                                                </th>
                                                {matrix.cells[rateIdx].map((cell) => {
                                                    const selected =
                                                        nearlyEqual(cell.rate, result.rate) &&
                                                        nearlyEqual(
                                                            cell.escalator,
                                                            result.escalator,
                                                            1e-4,
                                                        );
                                                    return (
                                                        <td key={`${cell.rate}-${cell.escalator}`}>
                                                            <button
                                                                type="button"
                                                                className={`pci-live-pricing__matrix-cell${
                                                                    selected
                                                                        ? ' pci-live-pricing__matrix-cell--on'
                                                                        : ''
                                                                }${
                                                                    !cell.available
                                                                        ? ' pci-live-pricing__matrix-cell--off'
                                                                        : ''
                                                                }`}
                                                                disabled={!cell.available}
                                                                aria-pressed={selected}
                                                                title={
                                                                    cell.available
                                                                        ? `Y1 ${formatMoney(cell.estimatedMonthly, 0)} · PPW $${cell.ppw?.toFixed(2)} · EPC ${formatMoney(cell.epc, 0)}`
                                                                        : 'Unavailable'
                                                                }
                                                                onClick={() => selectCell(cell)}
                                                            >
                                                                <span className="pci-live-pricing__matrix-cell-line">
                                                                    <em>Y1</em>
                                                                    <strong>
                                                                        {formatMoney(
                                                                            cell.estimatedMonthly,
                                                                            0,
                                                                        )}
                                                                    </strong>
                                                                </span>
                                                                <span className="pci-live-pricing__matrix-cell-line">
                                                                    <em>PPW</em>
                                                                    <strong>
                                                                        {cell.ppw != null
                                                                            ? `$${cell.ppw.toFixed(2)}`
                                                                            : '—'}
                                                                    </strong>
                                                                </span>
                                                                <span className="pci-live-pricing__matrix-cell-line">
                                                                    <em>EPC</em>
                                                                    <strong>
                                                                        {formatMoney(cell.epc, 0)}
                                                                    </strong>
                                                                </span>
                                                            </button>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            <p className="pci-live-pricing__matrix-legend">
                                Click a cell to set rate + escalator. Y1 monthly is the same across
                                escalators for a given rate; PPW and EPC change.
                            </p>
                        </div>
                    )}
                </>
            )}

            <p className="pci-live-pricing__footnote">
                Y1 monthly ≈ rate × production ÷ 12. PPW = slope × multiplier + intercept from the
                Excel row for that rate + escalator.
            </p>
        </section>
    );
};

export default PreContractLivePricingRail;
